/**
 * IGRA-393 — метки рекламных кампаний (utm) из адреса в cookie.
 *
 * Зачем это здесь, если платформа уже умеет то же самое. Легаси-бутстрап
 * (`kernel/config/main.php`) на каждом запросе с `utm_` в query зовёт
 * `LeadsUtmVisitsComponent::setVisit()`, и тот раскладывает параметры по cookie
 * `space_<имя>`. Но у igra.now фронт и бэк разделены: SPA отдаётся отдельно, и
 * адрес с метками легаси-бутстрап может не увидеть вовсе. Поэтому cookie ставит
 * фронт — В ТОМ ЖЕ ФОРМАТЕ, чтобы бэкенд читал их штатно и не знал, кто именно
 * их записал (`IgraNow/behaviors/PromoSource` читает `space_utm_source`).
 *
 * Белого списка значений здесь НЕТ, и это осознанно: фронт только сохраняет
 * метки, а решение «выдавать ли за них деньги» принимает бэкенд. Решение о
 * деньгах, живущее в коде, который отдаётся в браузер, решением не является.
 *
 * localStorage не дублируем: единственный читатель меток — PHP, а он видит
 * только cookie.
 */

/** Префикс cookie платформы: `utm_source` → `space_utm_source`. */
export const UTM_COOKIE_PREFIX = 'space_'

const UTM_QUERY_PREFIX = 'utm_'

const COOKIE_TTL_DAYS = 30

/**
 * Имя cookie нельзя ни экранировать, ни процентно закодировать — оно уходит в
 * заголовок как есть, поэтому ключ с точкой с запятой («`utm_x; Domain=evil`»)
 * был бы прямой инъекцией атрибутов. Реальные utm-метки в этот шаблон
 * укладываются все, так что ограничение ничего не стоит.
 */
const UTM_KEY = /^utm_[a-zA-Z0-9_]+$/

/**
 * Санитайзер значения — тот же символьный класс, что у платформы
 * (`LeadsUtmVisitsComponent::setVisit()`:
 * `preg_replace('#[^a-zA-Z0-9\._\{\}\[\]:;\|-]#is', '', $val)`). Повторён
 * буквально, а не «на свой вкус»: cookie читается как платформенная, и значение,
 * записанное фронтом, обязано совпадать с тем, что записал бы бэкенд по тому же
 * адресу.
 */
const UNSAFE_VALUE_CHARS = /[^a-zA-Z0-9._{}[\]:;|-]/g

export function sanitizeUtmValue(value: string): string {
  return value.replace(UNSAFE_VALUE_CHARS, '')
}

/**
 * Кладёт одну метку в cookie на 30 дней.
 *
 * Значение кодируется `encodeURIComponent` уже ПОСЛЕ санитайза: в разрешённом
 * платформой наборе остаются `;` и `|`, а неэкранированная точка с запятой
 * обрывает cookie. PHP разворачивает кодирование сам, читая `$_COOKIE`.
 */
export function persistUtmMark(key: string, value: string, doc: Document = document): void {
  if (!UTM_KEY.test(key)) {
    return
  }

  const safeValue = sanitizeUtmValue(value)
  if (safeValue === '') {
    return
  }

  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + COOKIE_TTL_DAYS)

  doc.cookie =
    `${UTM_COOKIE_PREFIX}${key}=${encodeURIComponent(safeValue)}` +
    `; expires=${expiryDate.toUTCString()}; path=/`
}

/**
 * Читает уже записанную метку. Пустая строка — метки нет.
 *
 * Чтение обёрнуто в try/catch по той же причине, что и запись согласия
 * (`shared/consent.ts`): приватное окно и «блокировать данные сайтов» бросают
 * на самом обращении к `document.cookie`, а не возвращают пустоту.
 */
export function readUtmMark(key: string, doc: Document = document): string {
  if (!UTM_KEY.test(key)) {
    return ''
  }

  const name = `${UTM_COOKIE_PREFIX}${key}=`

  try {
    const hit = doc.cookie.split('; ').find((part) => part.startsWith(name))

    return hit === undefined ? '' : decodeURIComponent(hit.slice(name.length))
  } catch {
    return ''
  }
}

/**
 * IGRA-TEAM — ставит метку, ТОЛЬКО если её ещё нет.
 *
 * Нужно собственным лендингам, у которых источник — сам факт открытия
 * страницы, а не хвост в адресе: `/team` лидер диктует голосом на вебинаре,
 * и `?utm_source=team` в такой ссылке не переживает пересказ. Реклама по-
 * прежнему приводит с хвостом, и тогда эта функция молчит.
 *
 * Первый источник выигрывает: человек, пришедший по рекламной ссылке и уже
 * посчитанный за ней, не имеет права переписаться на страницу, до которой
 * доскроллил потом. Перезапись здесь означала бы, что сегмент называет
 * последнее касание, а сравнивают сегменты по точке входа.
 */
export function ensureUtmMark(key: string, value: string, doc: Document = document): void {
  if (readUtmMark(key, doc) !== '') {
    return
  }

  try {
    persistUtmMark(key, value, doc)
  } catch {
    // Запись cookie бросает там же, где бросает чтение: изолированный
    // источник, «блокировать данные сайтов». Собственный лендинг не имеет
    // права падать из-за метки аналитики — сегмент важен, страница важнее.
    // В `persistUtmMark` эту обёртку не переносим: у него другой вызывающий
    // (`captureUtmMarksFromUrl` на старте роутера) и своё поведение, менять
    // которое эта задача не просила.
  }
}

/**
 * Снимает все `utm_*` с адреса, по которому человека привели.
 *
 * Вызывается один раз при старте роутера, до любой навигации: рекламная ссылка
 * ведёт на обычный лендинг или сразу на тариф, то есть «своего» роута у меток
 * нет и `beforeEnter` их бы не поймал.
 */
export function captureUtmMarksFromUrl(
  search: string = window.location.search,
  doc: Document = document,
): void {
  new URLSearchParams(search).forEach((value, key) => {
    if (key.startsWith(UTM_QUERY_PREFIX)) {
      persistUtmMark(key, value, doc)
    }
  })
}
